import { useEffect, useRef, useState, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Play,
  Volume2,
  VolumeX,
  Maximize2,
  Instagram,
} from "lucide-react";
import { useI18n } from "../../lib/i18n.jsx";
import Reveal from "../Reveal.jsx";

const VIDEOS = [
  { src: "/videos/camp-life/camp-life-1.webm", poster: "/videos/camp-life/camp-life-1-poster.jpg" },
  { src: "/videos/camp-life/camp-life-2.webm", poster: "/videos/camp-life/camp-life-2-poster.jpg" },
  { src: "/videos/camp-life/camp-life-3.webm", poster: "/videos/camp-life/camp-life-3-poster.jpg" },
  { src: "/videos/camp-life/camp-life-4.webm", poster: "/videos/camp-life/camp-life-4-poster.jpg" },
];

const IG_HANDLE = "scootive_camp";
const IG_URL = `https://instagram.com/${IG_HANDLE}`;

function offsetOf(i, index, n) {
  return ((i - index + n + Math.floor(n / 2)) % n) - Math.floor(n / 2);
}

function enterFullscreen(videoEl) {
  if (!videoEl) return;
  // Show native controls for the duration of fullscreen.
  videoEl.controls = true;
  // iOS Safari: dedicated path that triggers the OS-level player.
  if (typeof videoEl.webkitEnterFullscreen === "function") {
    try {
      videoEl.webkitEnterFullscreen();
      return;
    } catch (e) {
      /* fall through */
    }
  }
  const req =
    videoEl.requestFullscreen ||
    videoEl.webkitRequestFullscreen ||
    videoEl.mozRequestFullScreen ||
    videoEl.msRequestFullscreen;
  if (req) {
    const p = req.call(videoEl);
    if (p && typeof p.catch === "function") p.catch(() => {});
  }
}

export default function CampLife({ embed = false }) {
  const { t } = useI18n();
  const sectionRef = useRef(null);
  const videoRefs = useRef([]);
  const [index, setIndex] = useState(0);
  const [inView, setInView] = useState(false);
  const [autoplayAllowed, setAutoplayAllowed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [fsIndex, setFsIndex] = useState(-1); // which slide is currently in native FS

  const next = useCallback(() => setIndex((i) => (i + 1) % VIDEOS.length), []);
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + VIDEOS.length) % VIDEOS.length),
    []
  );

  // Lazy mount + autoplay trigger when section is on screen.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      setAutoplayAllowed(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            if (e.intersectionRatio >= 0.4) setAutoplayAllowed(true);
          } else if (e.intersectionRatio < 0.05) {
            setAutoplayAllowed(false);
          }
        }
      },
      { threshold: [0, 0.05, 0.4, 0.7] }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  // Drive playback for the active inline video.
  useEffect(() => {
    if (!inView) return;
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      v.muted = muted;
      if (i === index && autoplayAllowed) {
        const p = v.play();
        if (p && typeof p.then === "function") {
          p.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        }
      } else {
        v.pause();
        if (i !== index) {
          try {
            v.currentTime = 0;
          } catch (e) {
            /* ignore */
          }
        }
      }
    });
  }, [index, inView, autoplayAllowed, muted]);

  // Reset slide playback time when the index changes.
  useEffect(() => {
    const v = videoRefs.current[index];
    if (v) {
      try {
        v.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
    }
  }, [index]);

  // Hide native controls again when user exits fullscreen.
  useEffect(() => {
    const onFsChange = () => {
      const fs =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement;
      if (!fs) {
        videoRefs.current.forEach((v) => {
          if (v) v.controls = false;
        });
        setFsIndex(-1);
      }
    };
    const onIosEnd = () => {
      videoRefs.current.forEach((v) => {
        if (v) v.controls = false;
      });
      setFsIndex(-1);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    // iOS dispatches webkitendfullscreen on the <video> element itself.
    videoRefs.current.forEach((v) => {
      if (v) v.addEventListener("webkitendfullscreen", onIosEnd);
    });
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      videoRefs.current.forEach((v) => {
        if (v) v.removeEventListener("webkitendfullscreen", onIosEnd);
      });
    };
  }, []);

  // Keyboard nav, only when section is visible.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    let visible = false;
    const obs = new IntersectionObserver(
      (entries) => {
        visible = entries.some((e) => e.isIntersecting);
      },
      { threshold: 0.35 }
    );
    obs.observe(node);
    const onKey = (e) => {
      if (!visible) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      // Don't hijack arrows while in native fullscreen — let the player handle them.
      const fs =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement;
      if (fs) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      obs.disconnect();
    };
  }, [next, prev]);

  const onActiveClick = () => {
    const v = videoRefs.current[index];
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    setMuted((m) => !m);
  };

  const openFullscreen = (e) => {
    e.stopPropagation();
    setFsIndex(index);
    enterFullscreen(videoRefs.current[index]);
  };

  const stack = (
    <>
      <div className="relative h-[560px] sm:h-[600px] md:h-[680px]">
        {VIDEOS.map((v, i) => {
          const off = offsetOf(i, index, VIDEOS.length);
          const isActive = off === 0;
          const isSide = Math.abs(off) === 1;
          const shouldMount = inView && (isActive || isSide);

          const transform = isActive
            ? "translate3d(-50%, 0, 0) scale(1)"
            : isSide
            ? `translate3d(calc(-50% + ${off * 70}%), 0, 0) scale(0.78)`
            : `translate3d(calc(-50% + ${off * 95}%), 0, 0) scale(0.6)`;
          const opacity = isActive ? 1 : isSide ? 0.55 : 0;
          const z = isActive ? 20 : isSide ? 10 : 0;

          return (
            <div
              key={v.src}
              data-testid={`camp-life-slide-${i}`}
              aria-hidden={!isActive}
              onClick={
                isActive
                  ? onActiveClick
                  : isSide
                  ? () => setIndex(i)
                  : undefined
              }
              className={`absolute top-0 left-1/2 h-full aspect-[9/16] rounded-3xl overflow-hidden bg-winter shadow-2xl will-change-transform transition-all duration-500 ease-out ${
                isSide ? "hidden md:block cursor-pointer" : ""
              } ${isActive ? "cursor-pointer" : ""}`}
              style={{
                transform,
                opacity,
                zIndex: z,
                pointerEvents: isActive || isSide ? "auto" : "none",
                filter: isSide ? "saturate(0.85)" : "none",
              }}
            >
              {shouldMount ? (
                <video
                  ref={(el) => (videoRefs.current[i] = el)}
                  className={`w-full h-full ${
                    i === fsIndex ? "object-contain bg-black" : "object-cover"
                  }`}
                  preload={isActive ? "auto" : "metadata"}
                  poster={v.poster}
                  muted={muted}
                  loop
                  playsInline
                  data-testid={`camp-life-video-${i}`}
                >
                  <source src={v.src} type="video/webm" />
                </video>
              ) : (
                <img
                  src={v.poster}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              )}

              {/* Active-only top-right controls cluster */}
              {isActive && (
                <div className="absolute top-3 right-3 flex items-center gap-2 z-20">
                  <button
                    type="button"
                    onClick={toggleMute}
                    data-testid="camp-life-mute"
                    aria-label={muted ? t.camp_life.unmute : t.camp_life.mute}
                    aria-pressed={!muted}
                    className="w-10 h-10 rounded-full bg-winter/70 hover:bg-winter text-cream flex items-center justify-center backdrop-blur-sm shadow transition-colors"
                  >
                    {muted ? (
                      <VolumeX className="w-4 h-4" />
                    ) : (
                      <Volume2 className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={openFullscreen}
                    data-testid="camp-life-fullscreen"
                    aria-label={t.camp_life.fullscreen}
                    className="w-10 h-10 rounded-full bg-winter/70 hover:bg-winter text-cream flex items-center justify-center backdrop-blur-sm shadow transition-colors"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Active-only IG watermark (bottom-left) — icon-only on mobile, full pill on md+ */}
              {isActive && (
                <a
                  href={IG_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="camp-life-ig-link"
                  aria-label={`Instagram @${IG_HANDLE}`}
                  className="absolute bottom-3 left-3 z-20 flex items-center gap-2 rounded-full bg-winter/70 hover:bg-winter text-cream backdrop-blur-sm shadow transition-colors w-9 h-9 md:w-auto md:h-auto md:px-3 md:py-1.5 justify-center"
                >
                  <Instagram className="w-3.5 h-3.5" />
                  <span className="hidden md:inline text-xs font-semibold">
                    @{IG_HANDLE}
                  </span>
                </a>
              )}

              {/* Tap-to-play overlay (active only, when paused) */}
              {isActive && !isPlaying && (
                <span
                  data-testid="camp-life-play-overlay"
                  className="absolute inset-0 flex items-center justify-center bg-winter/30 pointer-events-none"
                >
                  <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-cream/90 flex items-center justify-center shadow-xl">
                    <Play
                      className="w-7 h-7 md:w-8 md:h-8 text-winter"
                      fill="currentColor"
                    />
                  </span>
                </span>
              )}

              {isSide && (
                <span className="absolute inset-0 bg-winter/40 pointer-events-none" />
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={prev}
          data-testid="camp-life-prev"
          aria-label={t.camp_life.prev}
          className="absolute z-30 left-2 md:left-4 top-1/2 -translate-y-1/2 bg-cream/95 hover:bg-summer hover:text-cream text-winter w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-colors shadow-lg"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={next}
          data-testid="camp-life-next"
          aria-label={t.camp_life.next}
          className="absolute z-30 right-2 md:right-4 top-1/2 -translate-y-1/2 bg-cream/95 hover:bg-summer hover:text-cream text-winter w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-colors shadow-lg"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <div className="absolute z-30 bottom-3 left-1/2 -translate-x-1/2 bg-winter/85 text-cream text-xs font-mono px-3 py-1.5 rounded-full">
          {index + 1} / {VIDEOS.length}
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mt-6">
        {VIDEOS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            data-testid={`camp-life-dot-${i}`}
            aria-label={`${t.camp_life.slide_label} ${i + 1}`}
            aria-current={i === index ? "true" : "false"}
            className={`h-2.5 rounded-full transition-all ${
              i === index ? "w-8 bg-summer" : "w-2.5 bg-winter/20 hover:bg-winter/40"
            }`}
          />
        ))}
      </div>
    </>
  );

  if (embed) {
    return (
      <div ref={sectionRef} data-testid="camp-life-section" className="overflow-hidden">
        {stack}
      </div>
    );
  }

  return (
    <section
      ref={sectionRef}
      id="camp-life"
      data-testid="camp-life-section"
      className="bg-cream py-20 md:py-28 overflow-hidden"
    >
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <Reveal as="div" className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
          <p className="font-handwritten text-summer text-2xl md:text-3xl mb-2">
            {t.camp_life.kicker}
          </p>
          <h2
            className="font-display font-bold text-winter leading-tight mb-4"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
          >
            {t.camp_life.title}
          </h2>
          <p className="text-lg text-winter/65">{t.camp_life.subtitle}</p>
        </Reveal>
        {stack}
      </div>
    </section>
  );
}
